<style lang="scss">
.tz-spinner {
  &__spin-circle {
    overflow: visible;
    animation: spin 1.5s linear infinite;

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }

  &__loader-container {
    max-width: 50px;
    max-height: 50px;
    position: relative;
  }

  &__loader {
    transform: rotate(-90deg);
  }

  &__image {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>

<template>
  <div class="tz-spinner">
    <div v-if="isCheck">
      <svg
        class="tz-spinner__checkmark"
        :width="checkSize"
        :height="checkSize"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          :fill="checkColor"
          :stroke="checkColor"
          stroke-width="1"
          d="M18 4.56189C18 4.85951 17.8839 5.15713 17.6748 5.37142L7.68774 15.6095C7.47871 15.8238 7.18839 15.9428 6.89806 15.9428C6.60774 15.9428 6.31742 15.8238 6.10839 15.6095L0.325161 9.68094C0.116129 9.46665 0 9.16904 0 8.87142C0 8.5738 0.116129 8.27618 0.325161 8.06189L1.90452 6.44284C2.11355 6.22856 2.40387 6.10951 2.69419 6.10951C2.98452 6.10951 3.27484 6.22856 3.48387 6.44284L6.89806 9.95475L14.5161 2.13332C14.7252 1.91904 15.0155 1.79999 15.3058 1.79999C15.5961 1.79999 15.8865 1.91904 16.0955 2.13332L17.6748 3.75237C17.8839 3.96665 18 4.26427 18 4.56189Z"
        />
      </svg>
    </div>

    <div v-else>
      <svg
        v-if="spinnerAnimation || (isContinualSpin && spinnerAnimation)"
        class="tz-spinner__spin-circle"
        :width="spinnerSize"
        :height="spinnerSize"
        viewBox="0 0 38 38"
        xmlns="http://www.w3.org/2000/svg"
        :style="{
          animationDelay: `${spinnerDelayConversion}s`
        }"
      >
        <g fill="none" fill-rule="evenodd">
          <g transform="translate(1 1)" :stroke-width="spinnerStroke">
            <circle
              v-if="spinnerAnimation"
              stroke="#E6E8EE"
              stroke-opacity=".5"
              cx="18"
              cy="18"
              r="18"
            />
            <path
              v-if="spinnerAnimation && isStartingSpin"
              :stroke="spinnerColor"
              d="M36 18c0-9.94-8.06-18-18-18"
            />
          </g>
        </g>
      </svg>
    </div>

    <div class="tz-spinner__loader-container" v-if="isFullCircle">
      <slot name="image" />
      <svg
        class="tz-spinner__loader"
        viewBox="0 0 106 106"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
      >
        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
          <g transform="translate(-17.000000, -17.000000)">
            <circle
              v-if="isFullCircle && isShowingBackground"
              class="tz-spinner__background-circle"
              stroke="#E6E8EE"
              :stroke-width="spinnerStroke"
              fill-rule="nonzero"
              cx="70"
              cy="70"
              r="50"
            ></circle>
            <circle
              v-if="isFullCircle && isShowingLoader"
              class="tz-spinner__progress-circle"
              :stroke="spinnerColor"
              :stroke-width="spinnerStroke"
              fill-rule="nonzero"
              :stroke-dasharray="circumference + ' ' + circumference"
              :style="{
                strokeDashoffset: strokeDashoffset,
                transition: 'ease .4s'
              }"
              cx="70"
              cy="70"
              r="50"
            ></circle>
          </g>
        </g>
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TzSpinner',
  props: {
    isCheck: {
      type: Boolean,
      default: false
    },
    spinnerStroke: {
      type: String,
      required: true
    },
    checkColor: {
      type: String
    },
    spinnerColor: {
      type: String,
      required: true
    },
    spinnerSize: {
      type: String
    },
    isStartingSpin: {
      type: Boolean,
      default: false
    },
    checkSize: {
      type: String
    },
    spinnerAnimation: {
      type: Boolean,
      required: true
    },
    animationStyle: {
      type: String,
      default: 'spin'
    },
    isShowingLoader: {
      type: Boolean
    },
    isShowingBackground: {
      type: Boolean
    },
    checkDelay: {
      type: Number,
      default: 7000
    },
    spinnerDelay: {
      type: Number,
      default: 1500
    },
    backgroundDelay: {
      type: Number,
      default: 3000
    },
    loaderDelay: {
      type: Number,
      default: 3000
    }
  },
  data() {
    const normalizedRadius = 60 - 4 * 2;

    const circumference = normalizedRadius * 2 * Math.PI;

    return {
      progress: 0,
      normalizedRadius,
      circumference
    };
  },
  computed: {
    isContinualSpin: function() {
      return this.animationStyle === 'spin';
    },
    isFullCircle: function() {
      return this.animationStyle === 'circle';
    },
    isSpinnerToCheck: function() {
      return this.animationStyle === 'check';
    },
    spinnerDelayConversion() {
      return this.spinnerDelay / 1000;
    },
    strokeDashoffset() {
      return this.circumference - (this.progress / 100) * this.circumference;
    }
  },
  methods: {
    setToCheck(delay) {
      if (!this.spinnerAnimation) {
        return;
      }
      if (this.isContinualSpin) {
        this.isCheck = false;
      }

      if (this.isSpinnerToCheck) {
        setTimeout(() => {
          this.$emit('tz-spinner-check');
        }, delay);
      }
    },
    onStart(delay, event, animationStyle) {
      if (!animationStyle) {
        return;
      }

      setTimeout(() => {
        this.$emit(event);
      }, delay);
    }
  },
  created() {
    this.setToCheck(this.checkDelay);
    this.onStart(
      this.backgroundDelay,
      'tz-spinner-loader-background',
      this.isFullCircle
    );
    this.onStart(this.loaderDelay, 'tz-spinner-loader-fill', this.isFullCircle);
    this.onStart(
      this.spinnerDelay,
      'tz-spinner-start-spin',
      this.isSpinnerToCheck
    );
  },
  mounted() {
    // emulating progress
    if (!this.isFullCircle) {
      return;
    }
    setTimeout(() => {
      const interval = setInterval(() => {
        this.progress += 10;
        if (this.progress === 100) {
          clearInterval(interval);
          this.$emit('tz-spinner-loader-done');
        }
      }, 150);
    }, this.loaderDelay);
  }
};
</script>
